import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import PageHeader from '../components/PageHeader';
import RewardDetailDrawer from '../components/drawers/RewardDetailDrawer';
import { useAdminData } from '../contexts/AdminDataContext';
import { AISidebar, AIRecommendationPanel, AIInlineHint } from '../components/ai';
import { Plus, Search, Filter, MoreVertical, Edit, Copy, Pause, Play, Trash2, Gift, Download, Calendar } from 'lucide-react';
const Rewards = () => {
  const navigate = useNavigate();
  const {
    rewards,
    updateReward,
    deleteReward,
    showToast
  } = useAdminData();
  const [statusFilter, setStatusFilter] = useState('Tutti');
  const [partnerFilter, setPartnerFilter] = useState('Tutti i Partners');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [isAISidebarOpen, setIsAISidebarOpen] = useState(false);
  const [selectedRewardId, setSelectedRewardId] = useState<string | null>(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rewardToDelete, setRewardToDelete] = useState<string | null>(null);
  const uniquePartners = useMemo(() => {
    const partners = rewards.map(r => r.partner);
    return ['Tutti i Partners', ...Array.from(new Set(partners))];
  }, [rewards]);
  const filteredRewards = useMemo(() => {
    return rewards.filter(reward => {
      const matchesStatus = statusFilter === 'Tutti' || reward.status === statusFilter;
      const matchesPartner = partnerFilter === 'Tutti i Partners' || reward.partner === partnerFilter;
      const matchesSearch = reward.name.toLowerCase().includes(searchTerm.toLowerCase());
      let matchesDateFrom = true;
      if (dateFrom && reward.startDate) {
        matchesDateFrom = new Date(reward.startDate) >= new Date(dateFrom);
      }
      let matchesDateTo = true;
      if (dateTo && reward.endDate) {
        matchesDateTo = new Date(reward.endDate) <= new Date(dateTo);
      }
      return matchesStatus && matchesPartner && matchesSearch && matchesDateFrom && matchesDateTo;
    });
  }, [rewards, statusFilter, partnerFilter, searchTerm, dateFrom, dateTo]);
  const handleCreateReward = () => {
    navigate('/rewards/new');
  };
  const handleExportCSV = () => {
    console.log('Esportazione CSV avviata');
    showToast('Esportazione CSV avviata');
  };
  const handleRowClick = (rewardId: string) => {
    setSelectedRewardId(rewardId);
    setShowDetailDrawer(true);
  };
  const handleCloseDetailDrawer = () => {
    setShowDetailDrawer(false);
    setTimeout(() => setSelectedRewardId(null), 300);
  };
  const handlePartnerClick = (e: React.MouseEvent, partnerId: string) => {
    e.stopPropagation();
    navigate(`/partners/${partnerId}`);
  };
  const handleToggleStatus = (rewardId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'Attivo' ? 'Inattivo' : currentStatus === 'Inattivo' ? 'Attivo' : currentStatus;
    updateReward(rewardId, {
      status: newStatus as any
    });
    setOpenMenuId(null);
  };
  const handleDuplicate = (rewardId: string) => {
    showToast('Premio duplicato');
    setOpenMenuId(null);
  };
  const handleDeleteClick = (rewardId: string) => {
    setRewardToDelete(rewardId);
    setShowDeleteModal(true);
    setOpenMenuId(null);
  };
  const handleConfirmDelete = () => {
    if (rewardToDelete) {
      deleteReward(rewardToDelete);
      setShowDeleteModal(false);
      setRewardToDelete(null);
    }
  };
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setRewardToDelete(null);
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Attivo':
        return 'bg-green-100 text-green-800';
      case 'Programmato':
        return 'bg-blue-100 text-blue-800';
      case 'Inattivo':
        return 'bg-amber-100 text-amber-800';
      case 'Scaduto':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  const calculateRedeemed = (reward: any) => {
    if (reward.availability.unlimited) return reward.redemptionsTotal;
    return reward.availability.total - reward.availability.current;
  };
  return <div className="flex h-screen bg-gray-50" data-id="element-3994">
      <div className="w-20 flex-shrink-0" data-id="element-3995">
        <Sidebar data-id="element-3996" />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-3997">
        <Topbar data-id="element-3998" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-3999">
          <div className="flex items-center justify-between mb-6" data-id="element-4000">
            <div data-id="element-4001">
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4002">Premi</h1>
              <p className="text-sm font-normal text-gray-500 mt-1" data-id="element-4003">
                Crea e gestisci le offerte disponibili per gli utenti.
              </p>
            </div>
            <div className="flex items-center gap-3" data-id="element-4004">
              <button onClick={handleExportCSV} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors" style={{
              borderRadius: '100px'
            }} data-id="element-4005">
                <Download className="h-4 w-4 mr-2" data-id="element-4006" />
                Esporta CSV
              </button>
              <button onClick={handleCreateReward} className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors" style={{
              borderRadius: '100px'
            }} data-id="element-4007">
                <Plus className="h-4 w-4 mr-2" data-id="element-4008" />
                Crea Premio
              </button>
            </div>
          </div>

          {/* Filters - Updated to rounded-sm */}
          <div className="mb-6 flex flex-wrap gap-3" data-id="element-4009">
            <div className="relative flex-1 min-w-[300px]" data-id="element-4010">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" data-id="element-4011" />
              <input type="text" placeholder="Cerca premio..." className="w-full rounded-sm border border-gray-300 pl-10 pr-4 py-2 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-green-600" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-4012" />
            </div>

            {/* Date From - Updated to rounded-sm */}
            <div className="flex items-center rounded-sm border border-gray-300 px-3 py-2 gap-2 hover:bg-gray-50 transition-colors" data-id="element-4013">
              <Calendar className="h-4 w-4 text-gray-500" data-id="element-4014" />
              <span className="text-sm font-normal text-gray-600" data-id="element-4015">Da:</span>
              <input type="date" className="bg-transparent text-sm font-normal focus:outline-none text-gray-700" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-id="element-4016" />
            </div>

            {/* Date To - Updated to rounded-sm */}
            <div className="flex items-center rounded-sm border border-gray-300 px-3 py-2 gap-2 hover:bg-gray-50 transition-colors" data-id="element-4017">
              <Calendar className="h-4 w-4 text-gray-500" data-id="element-4018" />
              <span className="text-sm font-normal text-gray-600" data-id="element-4019">A:</span>
              <input type="date" className="bg-transparent text-sm font-normal focus:outline-none text-gray-700" value={dateTo} onChange={e => setDateTo(e.target.value)} data-id="element-4020" />
            </div>

            <div className="flex items-center rounded-sm border border-gray-300 px-3 py-2 gap-2 hover:bg-gray-50 transition-colors" data-id="element-4021">
              <Filter className="h-4 w-4 text-gray-500" data-id="element-4022" />
              <select className="bg-transparent text-sm font-normal focus:outline-none text-gray-700" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-4023">
                <option value="Tutti" data-id="element-4024">Tutti gli stati</option>
                <option value="Attivo" data-id="element-4025">Attivo</option>
                <option value="Programmato" data-id="element-4026">Programmato</option>
                <option value="Inattivo" data-id="element-4027">Inattivo</option>
                <option value="Scaduto" data-id="element-4028">Scaduto</option>
              </select>
            </div>

            <div className="flex items-center rounded-sm border border-gray-300 px-3 py-2 gap-2 hover:bg-gray-50 transition-colors" data-id="element-4029">
              <Filter className="h-4 w-4 text-gray-500" data-id="element-4030" />
              <select className="bg-transparent text-sm font-normal focus:outline-none text-gray-700" value={partnerFilter} onChange={e => setPartnerFilter(e.target.value)} data-id="element-4031">
                {uniquePartners.map(partner => <option key={partner} value={partner} data-id="element-4032">
                    {partner}
                  </option>)}
              </select>
            </div>
          </div>

          {/* Table or Empty State - Updated rounded-sm */}
          {filteredRewards.length > 0 ? <div className="rounded-sm border border-gray-200 bg-white overflow-hidden" data-id="element-4033">
              <div className="overflow-x-auto" data-id="element-4034">
                <table className="w-full" data-id="element-4035">
                  <thead data-id="element-4036">
                    <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4037">
                      <th className="px-6 py-3" data-id="element-4038">Immagine</th>
                      <th className="px-6 py-3" style={{
                    minWidth: '200px'
                  }} data-id="element-4039">
                        Nome Premio
                      </th>
                      <th className="px-6 py-3 whitespace-nowrap" data-id="element-4040">Partner</th>
                      <th className="px-6 py-3" data-id="element-4041">Codice</th>
                      <th className="px-6 py-3" data-id="element-4042">Stato</th>
                      <th className="px-6 py-3" data-id="element-4043">Costo</th>
                      <th className="px-6 py-3" data-id="element-4044">Disponibilità</th>
                      <th className="px-6 py-3" data-id="element-4045">Riscatti</th>
                      <th className="px-6 py-3" data-id="element-4046">Inizio Validità</th>
                      <th className="px-6 py-3" data-id="element-4047">Fine Validità</th>
                      <th className="px-6 py-3 text-right" data-id="element-4048">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-4049">
                    {filteredRewards.map(reward => <tr key={reward.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleRowClick(reward.id)} data-id="element-4050">
                        <td className="px-6 py-4" data-id="element-4051">
                          <img src={reward.image} alt={reward.name} className="h-16 w-16 rounded-sm object-cover" data-id="element-4052" />
                        </td>
                        <td className="px-6 py-4" style={{
                    minWidth: '200px'
                  }} data-id="element-4053">
                          <div className="font-medium text-gray-900" data-id="element-4054">
                            {reward.name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-4055">
                          <button onClick={e => handlePartnerClick(e, reward.partnerId)} className="text-greenback hover:text-greenback/80 font-medium underline decoration-greenback decoration-2 underline-offset-2 transition-colors" data-id="element-4056">
                            {reward.partner}
                          </button>
                        </td>
                        <td className="px-6 py-4" data-id="element-4057">
                          <span className="font-mono text-sm text-gray-700" data-id="element-4058">
                            {reward.code}
                          </span>
                        </td>
                        <td className="px-6 py-4" data-id="element-4059">
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(reward.status)}`} data-id="element-4060">
                            {reward.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-medium" data-id="element-4061">{reward.cost}</td>
                        <td className="px-6 py-4" data-id="element-4062">
                          {reward.availability.unlimited ? <span className="text-sm font-normal text-gray-600" data-id="element-4063">
                              Illimitata
                            </span> : <span className="font-medium" data-id="element-4064">
                              {reward.availability.current} /{' '}
                              {reward.availability.total}
                            </span>}
                        </td>
                        <td className="px-6 py-4 font-medium" data-id="element-4065">
                          {calculateRedeemed(reward)}
                        </td>
                        <td className="px-6 py-4 text-sm font-normal text-gray-600" data-id="element-4066">
                          {formatDate(reward.startDate)}
                        </td>
                        <td className="px-6 py-4 text-sm font-normal text-gray-600" data-id="element-4067">
                          {formatDate(reward.endDate)}
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-4068">
                          <div className="relative inline-block" data-id="element-4069">
                            <button onClick={e => {
                        e.stopPropagation();
                        setOpenMenuId(openMenuId === reward.id ? null : reward.id);
                      }} className="p-1 hover:bg-gray-100 rounded" data-id="element-4070">
                              <MoreVertical className="h-5 w-5 text-gray-500" data-id="element-4071" />
                            </button>

                            {openMenuId === reward.id && <>
                                <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} data-id="element-4072" />
                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-sm shadow-lg border border-gray-200 z-20" data-id="element-4073">
                                  <button onClick={e => {
                            e.stopPropagation();
                            handleRowClick(reward.id);
                            setOpenMenuId(null);
                          }} className="w-full px-4 py-2 text-left text-sm font-normal hover:bg-gray-50 flex items-center" data-id="element-4074">
                                    <Edit className="h-4 w-4 mr-2" data-id="element-4075" />
                                    Modifica
                                  </button>
                                  <button onClick={e => {
                            e.stopPropagation();
                            handleToggleStatus(reward.id, reward.status);
                          }} className="w-full px-4 py-2 text-left text-sm font-normal hover:bg-gray-50 flex items-center" disabled={reward.status === 'Scaduto' || reward.status === 'Programmato'} data-id="element-4076">
                                    {reward.status === 'Attivo' ? <>
                                        <Pause className="h-4 w-4 mr-2" data-id="element-4077" />
                                        Disattiva
                                      </> : <>
                                        <Play className="h-4 w-4 mr-2" data-id="element-4078" />
                                        Attiva
                                      </>}
                                  </button>
                                  <button onClick={e => {
                            e.stopPropagation();
                            handleDuplicate(reward.id);
                          }} className="w-full px-4 py-2 text-left text-sm font-normal hover:bg-gray-50 flex items-center" data-id="element-4079">
                                    <Copy className="h-4 w-4 mr-2" data-id="element-4080" />
                                    Duplica
                                  </button>
                                  <button onClick={e => {
                            e.stopPropagation();
                            handleDeleteClick(reward.id);
                          }} className="w-full px-4 py-2 text-left text-sm font-medium hover:bg-red-50 flex items-center text-red-600 border-t border-gray-100" data-id="element-4081">
                                    <Trash2 className="h-4 w-4 mr-2" data-id="element-4082" />
                                    Elimina
                                  </button>
                                </div>
                              </>}
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div> : <div className="rounded-sm border border-gray-200 bg-white p-12" data-id="element-4083">
              <div className="text-center" data-id="element-4084">
                <Gift className="h-16 w-16 text-gray-300 mx-auto mb-4" data-id="element-4085" />
                <h2 className="text-xl font-semibold text-gray-700 mb-2" data-id="element-4086">
                  Nessun premio trovato
                </h2>
                <p className="text-sm font-normal text-gray-500 max-w-md mx-auto mb-6" data-id="element-4087">
                  Prova a modificare i filtri di ricerca.
                </p>
                <button onClick={() => {
              setSearchTerm('');
              setStatusFilter('Tutti');
              setPartnerFilter('Tutti i Partners');
              setDateFrom('');
              setDateTo('');
            }} className="text-sm font-medium text-green-600 hover:underline" data-id="element-4088">
                  Rimuovi tutti i filtri
                </button>
              </div>
            </div>}
        </main>
      </div>

      {/* AI Sidebar */}
      <AISidebar isOpen={isAISidebarOpen} onClose={() => setIsAISidebarOpen(false)} title="Suggerimenti AI per i tuoi premi" data-id="element-4089">
        <AIRecommendationPanel recommendations={[{
        id: '1',
        title: 'Set Attivi Puri (Fitoretinolo + Collagene)',
        type: 'Prodotto Viso',
        diamondRange: '60-70 crediti',
        reason: 'Trend in crescita: prodotti anti-age naturali +22%. Alta domanda per attivi concentrati e personalizzabili.',
        ctaLabel: 'Crea premio da qui',
        onCtaClick: () => navigate('/rewards/new')
      }, {
        id: '2',
        title: 'Ottimizza "Kit Cosmetici Solidi"',
        type: 'Ottimizzazione',
        diamondRange: '45 crediti (da 50)',
        reason: 'Disponibilità bassa (12/50) e trend +25%. Riduci costo di 5 crediti per aumentare accessibilità e velocizzare rotazione stock.',
        ctaLabel: 'Duplica e modifica',
        onCtaClick: () => {
          showToast('Premio duplicato con modifiche suggerite');
          setTimeout(() => navigate('/rewards/new'), 1000);
        }
      }]} data-id="element-4090" />
      </AISidebar>

      {/* Delete Confirmation Modal - Updated rounded-sm */}
      {showDeleteModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-4091">
          <div className="bg-white rounded-sm max-w-md w-full p-6" data-id="element-4092">
            <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-4093">
              Conferma Eliminazione
            </h2>
            <p className="text-sm font-normal text-gray-600 mb-6" data-id="element-4094">
              Sei sicuro di voler eliminare questo premio? Questa azione non può
              essere annullata.
            </p>
            <div className="flex gap-3 justify-end" data-id="element-4095">
              <button onClick={handleCancelDelete} className="px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 rounded-sm hover:bg-gray-50 transition-colors" data-id="element-4096">
                Annulla
              </button>
              <button onClick={handleConfirmDelete} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-sm hover:bg-red-700 transition-colors" data-id="element-4097">
                Elimina
              </button>
            </div>
          </div>
        </div>}

      {/* Reward Detail Drawer */}
      <RewardDetailDrawer isOpen={showDetailDrawer} onClose={handleCloseDetailDrawer} rewardId={selectedRewardId} data-id="element-4098" />
    </div>;
};
export default Rewards;